import {IconButton} from '@wandb/components';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'TextField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useDebounceState} from '../../../util/hooks';
import {getMenuItems} from '../hooks/useMenuItems';
import {SortOption, useSharedViewsItems} from '../hooks/useSharedViewsItems';
import {
  getWorkspacePreferences,
  setWorkspacePreference,
  WorkspacePreferences,
} from '../hooks/useWorkspacePreferences';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {EmptyPersonalWorkspaces} from './EmptyPersonalWorkspaces';
import {EmptySharedViews} from './EmptySharedViews';
import {NamedWorkspaceMenuGroup} from './NamedWorkspaceMenuGroup';
import {NamedWorkspaceSharedViews} from './NamedWorkspaceSharedViews';

export const NamedWorkspaceMenu = () => {
  const {sharedViews, personalWorkspaces, viewer, currentView} =
    useViewBarContext();
  const projectId = currentView?.project?.id ?? '';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldShowInfo, setShouldShowInfo] = useState(false);
  const [searchQuery, debouncedSearch, setSearchQuery] = useDebounceState(
    '',
    300
  );

  const preferences = getWorkspacePreferences(projectId);
  const [sortOption, setSortOption] = useState<SortOption>(
    preferences.sortOption || 'lastSaved'
  );
  const [shouldShowMyViewsOnly, setShouldShowMyViewsOnly] = useState(
    preferences.showMyViewsOnly ?? false
  );

  const hasAnyPersonalWorkspaces = Object.values(personalWorkspaces).length > 0;
  const hasAnySharedViews = Object.keys(sharedViews).length > 0;

  const [personalWorkspacesItems, personalWorkspacesItemsCount] =
    useMemo(() => {
      const items = getMenuItems(
        'personal',
        Object.values(personalWorkspaces),
        viewer?.username
      );
      return [
        items.filter(
          item =>
            !debouncedSearch ||
            item.createdBy.username
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase())
        ),
        items.length,
      ];
    }, [personalWorkspaces, viewer?.username, debouncedSearch]);

  const filteredShareViewItems = useSharedViewsItems(
    sharedViews,
    viewer?.username,
    sortOption,
    shouldShowMyViewsOnly,
    debouncedSearch
  );
  const filteredShareViewItemsCount = filteredShareViewItems.length;

  if (!hasAnySharedViews && !hasAnyPersonalWorkspaces) {
    // Hide menu button is there's nothing to display
    return null;
  }

  // Handler for updating preferences and state
  const updatePreference = (key: keyof WorkspacePreferences, value: any) => {
    setWorkspacePreference(projectId, key, value);
    if (key === 'sortOption') {
      setSortOption(value as SortOption);
    } else if (key === 'showMyViewsOnly') {
      setShouldShowMyViewsOnly(value as boolean);
    }
  };

  const resetFilters = () => {
    updatePreference('sortOption', 'lastSaved');
    updatePreference('showMyViewsOnly', false);
    setSearchQuery('');
  };

  return (
    <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenu.Trigger data-test="nw-menu-trigger" className="h-32">
        <IconButton
          icon="menu"
          variant="secondary"
          isActive={isMenuOpen}
          onPress={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
          aria-label="Toggle named workspace menu"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          data-test="nw-menu-content"
          className={twMerge(
            'z-[3]', // This is a hack that we really need to move away from
            'w-[20rem] max-w-[20rem]',
            'overflow-y-overlay md:translate-x-[68px]',
            'shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]'
          )}
          align="end"
          sideOffset={12}
          onCloseAutoFocus={e => {
            e.preventDefault();
          }}>
          <div className="border-b border-moon-100 p-8 dark:border-moon-800">
            <TextField
              value={searchQuery}
              onChange={setSearchQuery}
              onKeyDown={(_, e) => e.stopPropagation()}
              placeholder="Search workspaces"
              icon="search"
              size="medium"
              aria-label="Search workspaces"
              variant="ghost"
              extraActions={
                searchQuery ? (
                  <IconButton
                    variant="ghost"
                    icon="close"
                    size="sm"
                    mr={4}
                    aria-label="Clear search"
                    onPress={() => setSearchQuery('')}
                  />
                ) : undefined
              }
              isContainerNightAware
            />
          </div>
          <div
            className={twMerge(
              personalWorkspacesItems.length + filteredShareViewItemsCount > 1
                ? 'max-h-[calc(100vh-24rem)] custom-height-nw:max-h-[calc(100vh-15rem)]'
                : 'max-h-fit',
              'min-h-[3rem] overflow-auto'
            )}>
            {hasAnyPersonalWorkspaces && (
              <>
                <DropdownMenu.Label className="mb-8 mt-16 flex  flex-wrap items-center justify-between gap-16 px-12">
                  <div className="flex w-full flex-col gap-8 text-base leading-[24px]">
                    <div
                      className="flex items-center gap-4 font-semibold"
                      data-test="personal-workspace-label">
                      <div>Personal workspaces</div>
                      {debouncedSearch ? (
                        <div className="rounded bg-moon-500/[0.05] px-6 text-sm">
                          {`${personalWorkspacesItems.length} of ${personalWorkspacesItemsCount}`}
                        </div>
                      ) : (
                        <div className="rounded bg-moon-500/[0.05] px-6 text-sm">
                          {personalWorkspacesItemsCount}
                        </div>
                      )}
                    </div>
                    {personalWorkspacesItems.length > 0 ? (
                      <NamedWorkspaceMenuGroup
                        options={personalWorkspacesItems}
                        defaultMaxItems={debouncedSearch ? 5 : 1}
                        testId="personal-workspace-label"
                      />
                    ) : (
                      <EmptyPersonalWorkspaces />
                    )}
                  </div>
                </DropdownMenu.Label>
              </>
            )}
            <DropdownMenu.Separator />
            {hasAnySharedViews && (
              <NamedWorkspaceSharedViews
                sharedViewsItems={filteredShareViewItems}
                sortOption={sortOption}
                shouldShowMyViewsOnly={shouldShowMyViewsOnly}
                debouncedSearch={debouncedSearch}
                shouldShowInfo={shouldShowInfo}
                setShouldShowInfo={setShouldShowInfo}
                updatePreference={updatePreference}
              />
            )}
            {filteredShareViewItems.length === 0 && (
              <div className="flex justify-center">
                <EmptySharedViews
                  showClearFilterButton={hasAnySharedViews}
                  onClearFilters={resetFilters}
                />
              </div>
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
